import { useRef } from 'react';
import { useOutsideClick } from './Menu.hooks';

import styles from './Menu.module.css';

import { MenuButton, InstallButtonProps } from './Button';
import config from '@/config';
import { useDialog } from '@/hooks';

export function Menu(props: MenuProps) {
	const { isOpen, children, setIsMenuOpen } = props;
	const dialogRef = useRef(null);
	const ref = useRef(null);

	const closeDialog = () => {
		if (!dialogRef) {
			return;
		}
		if (!dialogRef.current) {
			return;
		}

		const dialog: HTMLDialogElement = dialogRef.current;

		dialog.setAttribute('data-hide', 'true');
		const listener = () => {
			dialog.removeAttribute('data-hide');
			dialog.close();
			setIsMenuOpen(false);
			dialog.removeEventListener('animationEnd', listener, false);
			dialog.removeEventListener('webkitAnimationEnd', listener, false);
		};
		dialog.addEventListener('animationEnd', listener, false);
		dialog.addEventListener('webkitAnimationEnd', listener, false);
	};

	useOutsideClick(
		ref,
		() => {
			if (isOpen) {
				closeDialog();
			}
		},
		'menu-btn',
	);

	const classNames = [styles['menu']];
	if (isOpen) {
		classNames.push(styles['open']);
	} else {
		classNames.push(styles['close']);
	}

	if (isOpen) {
		return (
			<dialog ref={dialogRef} open className={styles['menu-dialog']}>
				<div ref={ref} className={classNames.join(' ')}>
					{children}
				</div>
			</dialog>
		);
	}

	return (
		<dialog ref={dialogRef} className={styles['menu-dialog']}>
			<div ref={ref} className={classNames.join(' ')}>
				{children}
			</div>
		</dialog>
	);
}

interface MenuProps {
	isOpen: boolean;
	setIsMenuOpen: (a: boolean) => void;
	children?: JSX.Element | JSX.Element[];
}

export function ProfileMenu({
	open: isOpen,
	setOpen: setIsOpen,
	install,
}: ProfileMenuProps) {
	const { setShowDialog } = useDialog();
	if (!config?.app?.auth?.enabled) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	return (
		<Menu isOpen={isOpen} setIsMenuOpen={setIsOpen}>
			<MenuButton.SignIn />
			<MenuButton.SignUp />
			<MenuButton.Subscribe />
			<MenuButton.ManageSubscription />
			<MenuButton.Links links={config?.app?.menu?.links} />
			<MenuButton.SignOut />
			<MenuButton.Install
				show={install?.show}
				onClick={install?.onClick}
			/>
			<MenuButton.About
				onClick={() => {
					setShowDialog(true);
				}}
			/>
		</Menu>
	);
}

interface ProfileMenuProps {
	open: boolean;
	setOpen: (v: boolean) => void;
	install?: InstallButtonProps;
}
